<script lang="ts" setup>
import type { Agency, JsonLdCollection } from '~/types/api'
import { InternalRouteName } from '~/constants/internal-route-name'
import { getStructuredDataScript } from '~/utils/structured-data'

definePageMeta({
    layout: false,
    name: InternalRouteName.AGENCY_ENTITY,
})

const { $apiFetch } = useNuxtApp()
const image = useImage()
const route = useRoute()

const { data, error } = await useAsyncData<Agency>(async () => {
    const agencies = await $apiFetch<JsonLdCollection<Agency>>('/agencies', {
        query: {
            'slug': route.params.slug,
            'properties[]': 'id',
        },
    })
    if (!agencies?.['hydra:member']?.[0]?.id) {
        throw createError({ statusCode: 404, statusMessage: 'Agency not found', fatal: true })
    }
    return $apiFetch<Agency>('/agencies/' + agencies?.['hydra:member'][0].id)
})

if (error.value) {
    throw createError({ statusCode: 404, statusMessage: error.value?.message, fatal: true })
}

useDefaultAgencyState().value = data.value

const agency = computed(() => data.value)
const pageTitle = computed(() => t('agency.name', { name: agency.value?.title }))

const { t } = useI18n()

const { $getInternalPageUrl } = useNuxtApp()

const pageUrl = computed(() => {
    return $getInternalPageUrl({ name: InternalRouteName.AGENCY_ENTITY, params: { slug: agency.value?.slug } })
})

const directionUrl = computed(() => {
    if (!agency.value?.location?.geo?.latitude || !agency.value?.location?.geo?.longitude) {
        return false
    }
    return `https://www.google.com/maps/dir//${agency.value?.location?.geo?.latitude},${agency.value?.location?.geo?.longitude}`
})

const agencyAffinityButtons = computed(() => {
    return agency.value?.agencyAffinities?.map((agencyAffinity) => {
        return {
            id: agencyAffinity.id,
            label: agencyAffinity.title || agencyAffinity.slug,
            to: { name: InternalRouteName.AGENCY_ENTITY, params: { slug: agencyAffinity.slug } },
        }
    }).filter(a => a.id && a.label) || []
})

const autoRentalDataScript = getStructuredDataScript({
    '@context': 'https://schema.org',
    '@type': 'AutoRental',
    'name': agency.value?.title,
    'image': agency.value?.image
        ? image(agency.value?.image?.relativePath, { width: 1200 }, { provider: 'interventionRequest' })
        : undefined,
    'location': agency.value?.location,
    'address': agency.value?.location?.address,
    'telephone': agency.value?.telephone,
    'geo': agency.value?.location?.geo,
    'currenciesAccepted': 'EUR',
    'openingHoursSpecification': agency.value?.openingHoursSpecification,
    'url': pageUrl.value,
})

const { metaClient, metaServer } = usePageSeoMeta(agency, {
    title: pageTitle.value,
    description: t('agency.meta_description', { agency: agency.value?.title }),
})

useSeoMeta(metaClient)
useServerSeoMeta(metaServer)
useCacheControl({
    maxAge: 60 * 5, // 5 minutes
    sMaxAge: 60 * 30, // 30 minutes
    staleWhileRevalidate: 60 * 30,
    public: true,
})
</script>

<template>
    <NuxtLayout
        name="default"
        with-reservation-form
        offset-reservation-form
    >
        <template #header>
            <VWebPageHeader
                layout="default"
                :web-page="null"
                :title="pageTitle"
                img-src="/agency-banner.jpg"
                :class="$style.header"
                hide-reservation-dialog
            />
        </template>
        <template #default>
            <VPageStructuredData
                :agency="agency"
                :title="pageTitle"
            />
            <component
                :is="autoRentalDataScript"
                v-if="autoRentalDataScript"
            />
            <VColumnedContent
                v-if="agency"
                class="main-content-width--mobile-wider"
                :body-class="$style.body"
            >
                <template #aside>
                    <VButton
                        outlined
                        :label="$t('back_to_agencies')"
                        :class="$style.back"
                        :to="{ name: InternalRouteName.AGENCY_INDEX }"
                        :icon-last="false"
                        icon-name="chevron-left"
                    />
                    <VAgencyCard
                        v-slot="{ itemClass }"
                        :agency="agency"
                        direction="column"
                        :image-width="259"
                        :image-height="163"
                        :title="$t('agency.information_title')"
                    >
                        <VButton
                            :class="[$style['contact-button'], itemClass]"
                            filled
                            theme="yellow"
                            :to="{ name: InternalRouteName.CONTACT_INDEX, query: { mrcAgencyCode: agency.mrcAgencyCode } }"
                            :label="$t('contact_us')"
                            icon-name="arrow-small-right"
                        />
                    </VAgencyCard>
                    <template v-if="agency?.openingHoursSpecification?.length">
                        <div
                            class="text-h2-sm cap-black-italic"
                            :class="$style['agency__hours-title']"
                        >
                            {{ $t('agency.hours_title') }}
                        </div>
                        <VOpeningHours :hours="agency.openingHoursSpecification" />
                    </template>
                </template>
                <template #default>
                    <VMarkdown
                        v-if="agency?.text"
                        :content="agency.text"
                        :class="$style.text"
                    />
                    <div
                        :class="$style['map-title']"
                        class="text-h2-sm cap-black-italic"
                    >
                        {{ $t('agency.map_title') }}
                    </div>
                    <VAgenciesMap
                        id="map-single-stories"
                        locked
                        :selected-agency="agency"
                        :agencies="[agency]"
                    />
                    <p
                        v-if="directionUrl"
                        :class="$style['map-directions']"
                    >
                        <VButton
                            :href="directionUrl"
                            rel="nofollow noopener"
                            target="_blank"
                            size="s"
                            outlined
                            :label="$t('agency.get_directions_on_google_maps')"
                            icon-name="pin"
                        />
                    </p>
                    <template v-if="agencyAffinityButtons.length">
                        <h2 class="text-h2-sm cap-black-italic">
                            {{ $t('agency_affinities') }}
                        </h2>
                        <div :class="$style['affinity-buttons']">
                            <VButton
                                v-for="btnProps in agencyAffinityButtons"
                                :key="btnProps.id"
                                :to="btnProps.to"
                                :label="btnProps.label"
                                size="s"
                                outlined
                                icon-name="arrow-small-right"
                            />
                        </div>
                    </template>
                </template>
            </VColumnedContent>
        </template>
    </NuxtLayout>
</template>

<style lang="scss" module>
.header {
   --v-header-body-padding-bottom: calc(var(--v-reservation-offset-top) * -1 + #{rem(48)});
}

.title-info {
    display: block;
    margin-top: rem(12);
}

.back {
    margin-bottom: rem(32);
}

.media {
    display: block;
    overflow: hidden;
    width: rem(108);
    border-radius: 100vmax;
    margin-bottom: rem(32);

    img {
        width: 100%;
        height: auto;
    }
}

.agency__contact-title {
    margin-bottom: rem(20);
}

.contact-button {
    margin-top: rem(24);
}

.agency__hours-title {
    padding-top: rem(32);
    border-top: 1px solid color(black-10);
    margin-block: rem(32) rem(16);

    @include media('>=md') {
        padding-top: rem(42);
        margin-top: rem(42);
    }
}

.body {
    display: flex;
    flex-direction: column;
}

.text {
    order: 2;
    margin-top: rem(48);

    @include media('>=lg') {
        order: initial;
        margin-block: 0 rem(48);
    }
}

.map-title {
    margin-top: rem(16);
    margin-bottom: rem(16);

    @include media('>=lg') {
        margin-top: initial;
    }
}

.map-directions {
    margin-top: rem(16);
    margin-bottom: rem(32);
}

.affinity-buttons {
    display: flex;
    flex-wrap: wrap;
    margin-top: rem(12);
    gap: rem(12);
}
</style>
